import { useAuth0User } from '@/app/modules/Context/useAuth0User';
import JobFavouriteButton from '@/app/modules/JobFavouriteButton';
import { JobPointOfContact } from '@/app/modules/JobPointOfContact/JobPointOfContact';
import { getUtmShareUrl } from '@/app/utils/urlHelper';
import { JobApplicationStatusLabel } from '@/components/JobApplicationStatusLabel';
import { JobInformation } from '@/components/JobInformation';
import { RadiusShadowContainer } from '@/components/RadiusShadowContainer';
import ShareContainer from '@/components/ShareContainer';
import { PATH } from '@/constants/path';
import { JobButton } from '@/modules/JobButton';
import JobListingJobCardList from '@/modules/JobListingJobCardList';
import SimilarJobs from '@/modules/SimilarJobs';
import { ContainerStyle, UserLoggedinContainer } from '@/modules/UserLoggedinContainer';
import { getJobDetail, getSimilarJobs } from '@/services/jobService';
import { IJobDetail } from '@/types/jobs';
import { generateJobDetailsURL, generateLocationInfo } from '@/utils/jobListingHelper';
import { isJobExpired } from '@/utils/jobUtils';
import { textlengthLimit } from '@/utils/limitTextLength';
import { useAnalytics } from '@/utils/useAnalytics';
import cx from 'classnames';
import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import React, { useEffect } from 'react';

import { Button, Grid, Typography } from '@material-ui/core';

import styles from './style.module.scss';

export interface JobProps {
  // null means job not found
  detail: IJobDetail | null;
  isExpired?: boolean;
  similarJobs?: IJobDetail[];
}

export const generateDescription = (
  grade: string,
  speciality: string,
  locationSummary: string,
  jobNumber: string,
  clientDescriptionPlainText = '',
) => {
  //Removing tags //n
  const sanitizedDescription =
    clientDescriptionPlainText && clientDescriptionPlainText.replace(/\n/g, '');
  const description = `Medrecruit is seeking a ${speciality} - ${grade} in ${locationSummary}. ref# ${jobNumber}. `;
  return textlengthLimit(description.concat(sanitizedDescription), 160);
};

const generateTitle = (jobNumber: string, speciality: string) => {
  const jobNumberSuffix = ` - ${jobNumber}`;
  const spaceForSpecialty = 60 - jobNumberSuffix.length;
  return `${speciality.substring(0, spaceForSpecialty)}${jobNumberSuffix}`;
};
/**
 * The page will be used to render the job details page, jobNumber or UUID of job used to render the page
 * /jobs/grade/speciality/jnxxxxxxxx
 * /jobs/grade/jnxxxxxxxx
 * /jobs/jnxxxxxxxx
 */

const Job = ({ detail, isExpired, similarJobs }: JobProps) => {
  const router = useRouter();
  const track = useAnalytics();
  const { user } = useAuth0User();

  useEffect(() => {
    if (detail) {
      track('Job Details page loaded', {
        jobNumber: detail.jobNumber,
        open: !isExpired,
      });
    }
  }, [router.query]);

  if (!detail) {
    return (
      <UserLoggedinContainer style={ContainerStyle.DARK_BLUE_BANNER}>
        <div className={styles.unavailableHeader}>
          <h2>Sorry, unable to find this job listing.</h2>
          <Button
            className={styles.searchBtn}
            variant="contained"
            size="medium"
            color="secondary"
            onClick={() => {
              router.push(PATH.JOBS);
            }}
          >
            Return to job search
          </Button>
        </div>
      </UserLoggedinContainer>
    );
  }

  const {
    id,
    description,
    clientDescription,
    clientDescriptionPlainText,
    jobNumber,
    title,
    grade,
    speciality,
    subspeciality,
    isSaved,
    appliedStatus,
    accountName,
    displayClientName,
    clientNameMessage,
    workType,
    country,
  } = detail;
  const locationSummary = generateLocationInfo(detail.locationDisplay, detail.country, detail.city);
  const hasSimilarJobs = similarJobs && similarJobs.length > 0;
  const newLineEncoding = '%0d%0a';

  const seoSpecialtyText = speciality || '';
  const specialtyOrSubspecialityText = subspeciality ? subspeciality : seoSpecialtyText;

  const seoDescription = generateDescription(
    grade,
    specialtyOrSubspecialityText,
    locationSummary,
    jobNumber,
    clientDescriptionPlainText,
  );
  const seoTitle = generateTitle(jobNumber, specialtyOrSubspecialityText);
  const canonicalUrl = generateJobDetailsURL(jobNumber, grade, specialtyOrSubspecialityText);

  return (
    <>
      {isExpired && (
        <>
          <UserLoggedinContainer style={ContainerStyle.DARK_BLUE_BANNER} noFooterMargin>
            <NextSeo noindex={true} nofollow={true} />
            <div className={styles.unavailableHeader}>
              <h2>Sorry, this job listing is no longer available.</h2>
              <p>Job reference: {jobNumber}</p>
              <Button
                className={styles.searchBtn}
                variant="contained"
                size="medium"
                color="secondary"
                onClick={() => {
                  router.push(PATH.JOBS);
                }}
              >
                Return to job search
              </Button>
            </div>
            {hasSimilarJobs && (
              <>
                <div className={styles.similarJobTitle}>
                  <h2>Here are some other jobs that may be similar.</h2>
                </div>
                <div className={styles.similarJobs}>
                  <JobListingJobCardList jobs={similarJobs} suppressCTABanner={true} />
                </div>
              </>
            )}
          </UserLoggedinContainer>
        </>
      )}

      {!isExpired && (
        <UserLoggedinContainer style={ContainerStyle.DARK_BLUE_BANNER} noFooterMargin>
          <div className={styles.paddingBox}>
            <NextSeo
              title={seoTitle}
              description={seoDescription}
              noindex={true}
              nofollow={true}
              openGraph={{
                title: seoTitle,
                description: seoDescription,
                type: 'website',
              }}
            />
            <RadiusShadowContainer>
              <article>
                <div className={styles.article}>
                  <Grid container spacing={3}>
                    <Grid item md={7} xs={12} className={styles.jobDetailsContainer}>
                      <div className={styles.pill}>
                        <h1 className={styles.speciality}>{grade}</h1>
                      </div>
                      <h2 className={styles.detailTitle}>
                        {subspeciality ? subspeciality : speciality}
                      </h2>
                      {user && (
                        <p className={styles.locationAndClient}>
                          {displayClientName
                            ? accountName
                            : clientNameMessage || 'Client Name Withheld'}
                        </p>
                      )}
                      <p className={styles.locationAndClient} data-testid="job-location-details">
                        {locationSummary}
                      </p>

                      <div className={styles.actions}>
                        {appliedStatus ? (
                          <div className={styles.status}>
                            <JobApplicationStatusLabel status={appliedStatus} />
                          </div>
                        ) : (
                          <>
                            <JobButton
                              id={id}
                              jobNumber={jobNumber}
                              url={canonicalUrl}
                              className={styles.enquireButton}
                            />
                            <JobFavouriteButton
                              id={id}
                              jobNumber={jobNumber}
                              favourited={isSaved}
                              style="outlined"
                              className={styles.bannerButton}
                            />
                          </>
                        )}
                        <ShareContainer
                          text=""
                          jobOrArticleShare="jobshare"
                          id={id}
                          className={styles.shareBox}
                          buttonClassName={cx(styles.bannerButton, styles.iconButton)}
                          emailTitle={
                            <>
                              Who would you like <br /> to send this job to?
                            </>
                          }
                          emailSubject={`${
                            user?.given_name ?? 'Medrecruit'
                          } recommended a job for you`}
                          emailBody={`View the job recommended for you by ${
                            user?.given_name ?? 'Medrecruit'
                          } ${newLineEncoding}${encodeURI(
                            getUtmShareUrl(
                              'jobshare',
                              `${globalThis.location?.origin}${canonicalUrl}`,
                              'email',
                            ),
                          )}`}
                          facebookTitle={title}
                          shareTrackingProps={{
                            name: 'Share Job',
                            details: {
                              'Share Origin': 'Job Detail',
                              jobNumber: jobNumber,
                            },
                          }}
                        />
                      </div>
                    </Grid>

                    <Grid item md={5} xs={12}>
                      <JobPointOfContact
                        jobNumber={jobNumber}
                        placementType={workType}
                        isLoggedIn={!!user}
                        specialty={subspeciality ? subspeciality : speciality}
                        grade={grade}
                        countryCode={country}
                      />
                    </Grid>
                  </Grid>

                  <hr className={styles.hr} />

                  <Grid container spacing={3}>
                    <Grid item md={8} xs={12}>
                      <Typography
                        variant="subtitle1"
                        component="h3"
                        className={styles.detailSubTitle}
                      >
                        Job Description:
                      </Typography>
                      <div
                        className={styles.content}
                        dangerouslySetInnerHTML={{ __html: description || 'Not Available' }}
                      />
                      {clientDescription && (
                        <>
                          <Typography
                            variant="subtitle1"
                            component="h3"
                            className={styles.detailSubTitle}
                          >
                            Client Description:
                          </Typography>
                          <div
                            className={styles.content}
                            dangerouslySetInnerHTML={{
                              __html: `<p>${clientDescription}</p>`,
                            }}
                          />
                        </>
                      )}
                    </Grid>
                    <Grid item md={4} xs={12}>
                      <div className={styles.informationBox}>
                        <JobInformation job={detail} />
                      </div>
                      <SimilarJobs referringJobNumber={jobNumber} jobs={similarJobs || []} />
                    </Grid>
                    <Grid item md={8} xs={12}>
                      {!appliedStatus && (
                        <Grid item md={5} xs={12}>
                          <div className={styles.detailContentApply}>
                            <JobButton id={id} jobNumber={jobNumber} url={canonicalUrl} />
                          </div>
                        </Grid>
                      )}
                      <p className={styles.reference}>Job reference: {jobNumber}</p>
                    </Grid>
                  </Grid>
                </div>
              </article>
            </RadiusShadowContainer>
          </div>
        </UserLoggedinContainer>
      )}
    </>
  );
};

export async function getServerSideProps(
  ctx: GetServerSidePropsContext,
): Promise<GetServerSidePropsResult<JobProps>> {
  let id;
  const { details } = ctx.query;
  if (ctx.req?.url && decodeURIComponent(ctx.req.url) !== ctx.req.url) {
    console.warn(
      `Incorrectly encoded Jobs URL encountered; let marketing know that one of their links in ads or articles may be incorrect: ${ctx.req.url}`,
    );
    return {
      redirect: {
        destination: decodeURIComponent(ctx.req.url),
        permanent: false,
      },
    };
  }
  if (details) {
    id = details[details.length - 1];
  } else {
    return { notFound: true };
  }

  const [jobDetails, similarJobs] = await Promise.all([
    getJobDetail(id, ctx),
    getSimilarJobs(id, ctx),
  ]);

  let isExpired = false;
  if (jobDetails) {
    /**
     * If the page was requested by that non-canonical URL, perhaps via a link in an email sent by Salesforce, redirect
     * to the canonical URL which looks better to users and for bots to know that the old page no longer exists
     */
    if (details.length === 1) {
      const { grade, speciality, jobNumber, subspeciality } = jobDetails;
      const subspcialityOrSpeciality = subspeciality ? subspeciality : speciality;
      return {
        redirect: {
          destination: generateJobDetailsURL(jobNumber, grade, subspcialityOrSpeciality),
          permanent: true,
        },
      };
    }
    isExpired = isJobExpired(jobDetails);
  }

  return {
    props: {
      detail: jobDetails,
      isExpired,
      similarJobs: similarJobs || [],
    },
  };
}

export default Job;
